import { useEffect, useState } from "react";
import { CloseButton, Col, Row } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";
import styled from "styled-components";
import supportClient, { SupportConfig }  from "@apis/supportClient";
import SntLink from "../../../components/ReactBootstrap/SntLink";
import type { RootState } from "../../../types/RootState";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

import UserGuideIcon from "@images/common/user-guide.svg";
import ReleaseNoteIcon from "@images/common/release-note.svg";
import PartnerIcon from "@images/common/partner.svg";
import ContactSupportIcon from "@images/common/contact-support.svg";
import LearningCenter from "@images/common/Learning-center.svg";

const Styles = styled.div`
  .panel {
    margin-bottom: 16px;
  }
`;

interface HelpSidebarProps {
  orgId?: number;
  closeEvent: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const HelpSidebar = ({ orgId, closeEvent }: HelpSidebarProps) => {
  const loginInfo = useSelector((state: RootState) => state.user, shallowEqual);

  const [support, setSupport] = useState<SupportConfig>({
    helpPageSupportType: "sensolus",
    platformPlansSupportType: "sensolus",
    isHiddenSensolusLink: false,
    helpdeskType: "ZENDESK_SENSOLUS",
    helpdeskBaseUrl: "https://sensolussupport.zendesk.com",
    helpdeskDocUrl: "/rest/authentication/docs-login?redirect=/",
    helpdeskTicketUrl: "https://sensolussupport.zendesk.com/hc/en-us/requests/new",
  });

  useEffect(() => {
    if (orgId) {
      supportClient.get(orgId)
        .then(({ data }) => {
          setSupport(data);
        })
        .catch(() => {
          // Keep default state on error
        });
    }
  }, [orgId]);

  const onClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    closeEvent(e);
  };

  return (
    <>
      <Row style={{ alignItems: "center" }}>
        <Col className="mb-3" xs="9">
          <div
            style={{
              fontFamily: "Open Sans",
              fontStyle: "normal",
              fontWeight: "bold",
              fontSize: "14px",
              lineHeight: "20px",
              color: "#424B59",
            }}
          >
            {translate(LanguageKey.help_and_support)}
          </div>
        </Col>
        <Col xs="3" className="float-end">
          <CloseButton onClick={onClose}></CloseButton>
        </Col>
      </Row>

      <Styles>
        {support.helpPageSupportType === "sensolus" && (
          <div className="item">
            <div className="panel">
              <p
                style={{
                  fontFamily: "Open Sans",
                  fontStyle: "normal",
                  fontWeight: "bold",
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#424B59",
                }}
              >
                <img src={UserGuideIcon} width="32" height="32" alt="" />
                {" " + translate(LanguageKey.documentation_center_key)}
              </p>
              <p className="mb-0">
                {translate(LanguageKey.detail_information_platform_guide)}
              </p>
              <SntLink
                target="_blank"
                to={{ pathname: support.helpdeskDocUrl }}
              >
                {translate(LanguageKey.documentation_center_guide_key)}
              </SntLink>
            </div>
          </div>
        )}

        {support.helpPageSupportType === "sensolus" && (
          <>
            <hr className="snt-line" />
            <div className="item">
              <div className="panel">
                <p
                  style={{
                    fontFamily: "Open Sans",
                    fontStyle: "normal",
                    fontWeight: "bold",
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#424B59",
                  }}
                >
                  <img src={ReleaseNoteIcon} width="32" height="32" alt="" />
                  {" " + translate(LanguageKey.release_notes)}
                </p>
                <p className="mb-0">
                  {translate(LanguageKey.find_out_whats_new_in_the_platform)}
                </p>
                <SntLink
                  target="_blank"
                  to="/rest/authentication/docs-login?redirect=/docs/releases/release_notes"
                >
                  {translate(LanguageKey.read_the_release_notes)}
                </SntLink>
              </div>
            </div>
          </>
        )}

        {support.helpPageSupportType === "custom" && (
          <div className="item">
            <div className="panel">
              <>
                <p
                  style={{
                    fontFamily: "Open Sans",
                    fontStyle: "normal",
                    fontWeight: "bold",
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#424B59",
                  }}
                >
                  <img src={UserGuideIcon} width="32" height="32" alt="" />
                  {" " + support.helpPageTitle}
                </p>
                <div className="panel-body">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: support.helpPageDescription || "",
                    }}
                  />
                </div>
              </>
            </div>
          </div>
        )}

        {loginInfo?.organisation?.organisationType === "PARTNER" &&
          loginInfo?.partner?.commercialPartner && (
            <>
              <hr className="snt-line" />
              <div className="item">
                <div className="panel">
                  <p
                    style={{
                      fontFamily: "Open Sans",
                      fontStyle: "normal",
                      fontWeight: "bold",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#424B59",
                    }}
                  >
                    <img src={PartnerIcon} width="32" height="32" alt="" />
                    {" " + translate(LanguageKey.partner_portal_key)}
                  </p>
                  <p className="mb-0">
                    {translate(LanguageKey.partner_portal_description_key)}
                  </p>
                  <SntLink
                    target="_blank"
                    to="/rest/authentication/docs-login?redirect=/docs/partner/partner_resource_center"
                  >
                    {translate(LanguageKey.visit_the_partner_portal_key)}
                  </SntLink>
                </div>
              </div>
            </>
          )}

        <hr className="snt-line" />

        {support.helpdeskType === "CUSTOM_SUPPORT" ? (
          <>
            <div className="item">
              <div className="panel">
                <>
                  <p
                    style={{
                      fontFamily: "Open Sans",
                      fontStyle: "normal",
                      fontWeight: "bold",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#424B59",
                    }}
                  >
                    <img
                      src={ContactSupportIcon}
                      width="32"
                      height="32"
                      alt=""
                    />
                    {" " + support.helpdeskTitle}
                  </p>
                  <div className="panel-body">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: support.helpdeskDescription || "",
                      }}
                    />
                  </div>
                </>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="item">
              <div className="panel">
                <>
                  <p
                    style={{
                      fontFamily: "Open Sans",
                      fontStyle: "normal",
                      fontWeight: "bold",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#424B59",
                    }}
                  >
                    <img
                      src={ContactSupportIcon}
                      width="32"
                      height="32"
                      alt=""
                    />
                    {" " + translate(LanguageKey.contact_support_key)}
                  </p>
                  <p className="mb-0">
                    {translate(LanguageKey.do_you_have_questions_remarks)}
                  </p>
                  {loginInfo?.organisation?.organisationType === "PARTNER" ? (
                    <SntLink
                      target="_blank"
                      to={`/zendesk/sso?action=sidebar_click&menu=ticket&useDefaultHelpDesk=true&org=${loginInfo?.organisation.id}`}
                    >
                      {translate(LanguageKey.make_a_request)}
                    </SntLink>
                  ) : support.helpdeskType === "FRESHDESK" ? (
                    <SntLink
                      target="_blank"
                      to={{ pathname: support.helpdeskTicketUrl }}
                    >
                      {translate(LanguageKey.make_a_request)}
                    </SntLink>
                  ) : (
                    <SntLink
                      target="_blank"
                      to={`/zendesk/sso?action=sidebar_click&menu=ticket&org=${loginInfo?.organisation?.id}`}
                    >
                      {translate(LanguageKey.make_a_request)}
                    </SntLink>
                  )}
                </>
              </div>
            </div>
          </>
        )}
        {loginInfo.talentLMSUsername && (
          <div className="item">
            <div className="panel">
              <>
                <p
                  style={{
                    fontFamily: "Open Sans",
                    fontStyle: "normal",
                    fontWeight: "bold",
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#424B59",
                  }}
                >
                  <img src={LearningCenter} width="32" height="32" alt="" />
                  {" " + translate(LanguageKey.sensolus_academy)}
                </p>
                <div className="mb-0">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: translate(LanguageKey.join_sensolus_academy),
                    }}
                  />
                </div>
              </>
            </div>
          </div>
        )}
      </Styles>
    </>
  );
};

export default HelpSidebar;
